import React, { useState, useEffect,useContext } from 'react';
import { TextInput, Divider, Button, Tab, TabGroup, TabList } from '@tremor/react';
import { toast } from 'react-toastify';
import { ShopContext } from '../context/ShopContext';

const MAX_ADDRESSES = 10;
const LS_KEY = 'scc_addressbook';

const getInitialAddresses = () => {
  try {
    const data = localStorage.getItem(LS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const defaultForm = {
  name: '',
  phone: '',
  country: 'India',
  state: '',
  city: '',
  street: '',
  zip: '',
  isDefault: false,
  lat: '',
  lon: '',
};

const AddressBook = () => {
  const [addresses, setAddresses] = useState(getInitialAddresses());
  const [form, setForm] = useState(defaultForm);
  const [editingIdx, setEditingIdx] = useState(null);
  const [search, setSearch] = useState('');
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [tab, setTab] = useState(0);
  const { userInfo } = useContext(ShopContext);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(addresses));
  }, [addresses]);

  useEffect(() => {
    if (userInfo && !editingIdx && tab === 0) {
      setForm(f => ({
        ...f,
        name: userInfo.name || '',
        phone: userInfo.phone || '',
        country: 'India',
      }));
    }
  }, [userInfo, editingIdx, tab]);

  // Removed auto country effect and function

  const autoFillAddress = async () => {
    setAutoDetecting(true);
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      setAutoDetecting(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(async pos => {
      const { latitude, longitude } = pos.coords;
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
        const data = await res.json();
        const addr = data.address || {};
        setForm(f => ({
          ...f,
          country: addr.country || '',
          state: addr.state || '',
          city: addr.city || addr.town || addr.village || '',
          street: [addr.road, addr.neighbourhood, addr.suburb].filter(Boolean).join(', '),
          zip: addr.postcode || '',
          lat: latitude,
          lon: longitude,
        }));
        toast.success('Address auto-filled!');
      } catch {
        toast.error('Failed to auto-fill address');
      }
      setAutoDetecting(false);
    }, () => {
      toast.error('Location permission denied');
      setAutoDetecting(false);
    });
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = () => {
    if (!form.name || !form.phone || !form.country || !form.city || !form.street) {
      toast.error('Please fill all required fields');
      return;
    }
    if (editingIdx !== null) {
      const updated = addresses.map((a, i) => i === editingIdx ? { ...form } : a);
      setAddresses(updated);
      setEditingIdx(null);
      toast.success('Address updated');
    } else {
      if (addresses.length >= MAX_ADDRESSES) {
        toast.error('Maximum 10 addresses allowed');
        return;
      }
      setAddresses([...addresses, { ...form }]);
      toast.success('Address added');
    }
    setForm(defaultForm);
  };

  const handleEdit = idx => {
    setForm(addresses[idx]);
    setEditingIdx(idx);
    setTab(0);
  };

  const handleDelete = idx => {
    if (window.confirm('Delete this address?')) {
      setAddresses(addresses.filter((_, i) => i !== idx));
      toast.success('Address deleted');
    }
  };

  const handleSetDefault = idx => {
    setAddresses(addresses.map((a, i) => ({ ...a, isDefault: i === idx })));
    toast.success('Default address set');
  };

  const handleCopy = idx => {
    const a = addresses[idx];
    const text = `${a.name}, ${a.phone}, ${a.street}, ${a.city}, ${a.state}, ${a.country}, ${a.zip}`;
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleUseForOrder = idx => {
    localStorage.setItem('scc_selected_address', JSON.stringify(addresses[idx]));
    toast.success('Address ready for order');
  };

  const filtered = addresses.filter(a =>
    [a.name, a.phone, a.country, a.state, a.city, a.street, a.zip].join(' ').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="w-full">
      <h3 className="text-tremor-title font-bold text-tremor-content-strong dark:text-dark-tremor-content-strong mb-2">Address Book</h3>
      <p className="text-xs text-gray-500 mb-4">Manage your addresses here. Local to this device only.</p>
      <TabGroup index={tab} onIndexChange={setTab}>
        <TabList className="mb-4">
          <Tab className="!text-tremor-content-strong dark:!text-dark-tremor-content-strong !font-medium">Add / Edit Address</Tab>
          <Tab className="!text-tremor-content-strong dark:!text-dark-tremor-content-strong !font-medium">Saved Addresses</Tab>
        </TabList>
        <div className="mt-2">
          {tab === 0 && (
            <form className="space-y-3" onSubmit={e => { e.preventDefault(); handleSave(); }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextInput name="name" value={form.name} onChange={handleChange} placeholder="Full Name" required className="rounded-tremor-small !bg-tremor-background !text-tremor-content-strong dark:!bg-dark-tremor-background dark:!text-dark-tremor-content-strong" />
                <TextInput name="phone" value={form.phone} onChange={handleChange} placeholder="Phone" required className="rounded-tremor-small !bg-tremor-background !text-tremor-content-strong dark:!bg-dark-tremor-background dark:!text-dark-tremor-content-strong" />
                <TextInput name="country" value={form.country} placeholder="Country" required className="rounded-tremor-small !bg-tremor-background !text-tremor-content-strong dark:!bg-dark-tremor-background dark:!text-dark-tremor-content-strong" disabled />
                <TextInput name="state" value={form.state} onChange={handleChange} placeholder="State" className="rounded-tremor-small !bg-tremor-background !text-tremor-content-strong dark:!bg-dark-tremor-background dark:!text-dark-tremor-content-strong" />
                <TextInput name="city" value={form.city} onChange={handleChange} placeholder="City" required className="rounded-tremor-small !bg-tremor-background !text-tremor-content-strong dark:!bg-dark-tremor-background dark:!text-dark-tremor-content-strong" />
                <TextInput name="street" value={form.street} onChange={handleChange} placeholder="Street Address" required className="rounded-tremor-small !bg-tremor-background !text-tremor-content-strong dark:!bg-dark-tremor-background dark:!text-dark-tremor-content-strong" />
                <TextInput name="zip" value={form.zip} onChange={handleChange} placeholder="ZIP / Postal Code" className="rounded-tremor-small !bg-tremor-background !text-tremor-content-strong dark:!bg-dark-tremor-background dark:!text-dark-tremor-content-strong" />
              </div>
              <div className="flex flex-wrap gap-2 mt-1">
                {/* Auto Country button removed */}
                <Button type="button" onClick={autoFillAddress} loading={autoDetecting} className="bg-tremor-brand text-black !rounded-tremor-small">Auto Fill Address</Button>
                <Button type="submit" className="bg-tremor-brand text-black !rounded-tremor-small">{editingIdx !== null ? 'Update' : 'Add'} Address</Button>
                {editingIdx !== null && <Button type="button" className="!rounded-tremor-small" onClick={() => { setForm(defaultForm); setEditingIdx(null); }}>Cancel</Button>}
              </div>
            </form>
          )}
          {tab === 1 && (
            <div>
              <div className="flex items-center mb-3">
                <TextInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Search addresses..." className="rounded-tremor-small !bg-tremor-background !text-tremor-content-strong dark:!bg-dark-tremor-background dark:!text-dark-tremor-content-strong" />
                <span className="ml-2 text-xs text-gray-400">{filtered.length} / {addresses.length}</span>
              </div>
              <Divider className="!my-2" />
              <div className="space-y-3">
                {filtered.length === 0 && <div className="text-gray-400">No addresses found.</div>}
                {filtered.map((a, idx) => (
                  <div key={idx} className={`p-3 rounded-tremor-small border ${a.isDefault ? 'border-tremor-brand bg-tremor-brand/10' : 'border-gray-200 dark:border-dark-tremor-border'} flex flex-col sm:flex-row sm:items-center justify-between gap-2`}> 
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-base flex items-center gap-2 text-tremor-content-strong dark:text-dark-tremor-content-strong truncate">
                        {a.isDefault && <span className="text-xs bg-tremor-brand text-white px-2 py-0.5 rounded">Default</span>}
                        <span className="truncate">{a.name}</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{a.phone}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{a.street}, {a.city}, {a.state}, {a.country} {a.zip}</div>
                      {a.lat && a.lon && (
                        <iframe
                          title="Map preview"
                          src={`https://maps.google.com/maps?q=${a.lat},${a.lon}&z=15&output=embed`}
                          className="w-full h-20 mt-2 rounded"
                          loading="lazy"
                        />
                      )}
                    </div>
                    <div className="flex flex-col gap-1 min-w-[120px]">
                      <Button size="xs" className="bg-tremor-brand text-black !rounded-tremor-small" onClick={() => handleEdit(addresses.indexOf(a))}>Edit</Button>
                      <Button size="xs" className="bg-tremor-brand text-black !rounded-tremor-small" onClick={() => handleSetDefault(addresses.indexOf(a))}>Set Default</Button>
                      <Button size="xs" className="bg-tremor-brand text-black !rounded-tremor-small" onClick={() => handleCopy(addresses.indexOf(a))}>Copy</Button>
                      <Button size="xs" className="bg-red-500 text-black !rounded-tremor-small" onClick={() => handleDelete(addresses.indexOf(a))}>Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </TabGroup>
    </div>
  );
};

export default AddressBook;
